// PWA glue: service worker registration, "install prompt" capture, and an
// "update available" signal. Components consume this via the small hook
// exported at the bottom — keeps SW concerns out of the React tree.

const SW_PATH = "/pagevoice/sw.js";

type Listener = () => void;

interface PwaState {
  installPromptEvent: BeforeInstallPromptEvent | null;
  isStandalone: boolean;
  updateReady: boolean;
}

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const state: PwaState = {
  installPromptEvent: null,
  isStandalone:
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      // iOS Safari uses this non-standard flag.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window.navigator as any).standalone === true),
  updateReady: false,
};

const listeners = new Set<Listener>();

function notify() {
  for (const l of listeners) l();
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function getPwaState(): Readonly<PwaState> {
  return state;
}

export function canPrompt(): boolean {
  return !!state.installPromptEvent && !state.isStandalone;
}

export async function promptInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const e = state.installPromptEvent;
  if (!e) return "unavailable";
  try {
    await e.prompt();
    const choice = await e.userChoice;
    if (choice.outcome === "accepted") {
      state.installPromptEvent = null;
      notify();
    }
    return choice.outcome;
  } catch {
    return "unavailable";
  }
}

async function requestPersistentStorage(): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storage = (navigator as any).storage;
    if (!storage?.persist) return;
    const already = await storage.persisted?.();
    if (already) return;
    const granted = await storage.persist();
    console.info(
      "[pagevoice] persistent storage:",
      granted ? "granted" : "denied (will retry on next user gesture)"
    );
  } catch (err) {
    console.warn("[pagevoice] persistent storage request failed", err);
  }
}

export function init(): void {
  if (typeof window === "undefined") return;

  // Request persistent storage. Without this, iOS / Safari aggressively
  // purges WebKit-stored data (IndexedDB included) when the OS decides
  // it's low on space or the PWA hasn't been opened in 7+ days. With it,
  // the browser is supposed to ask the user before evicting. Best-effort
  // — many browsers grant silently; others require a user gesture first.
  void requestPersistentStorage();
  // Re-attempt on the first user gesture (Safari often denies the silent
  // request but grants once it knows the user is interacting). Removed
  // after one fire — it's idempotent and we don't want to spam.
  const retry = () => void requestPersistentStorage();
  window.addEventListener("pointerdown", retry, { once: true });
  window.addEventListener("keydown", retry, { once: true });

  // Detect when the user installs (standalone flips).
  window.addEventListener("appinstalled", () => {
    state.installPromptEvent = null;
    state.isStandalone = true;
    notify();
  });

  // Auto-apply updates: once a new build is ready, reload the next time the app
  // is brought back to the foreground — so users always get the latest without
  // hunting for a banner, and never mid-read (the page is hidden when they
  // switch away). Armed once when the first update appears. 2026-07-17.
  let autoReloadArmed = false;
  const armAutoReload = () => {
    if (autoReloadArmed) return;
    autoReloadArmed = true;
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible" && state.updateReady) {
        void applyUpdateAndReload();
      }
    });
  };

  // Register the service worker — ONLY for the canonical /pagevoice/
  // deployment. A staging build served under a different base (e.g.
  // /pagevoice-next/) must NOT register a SW: its SW file + scope are the
  // live /pagevoice/ ones, and we never want a test build to touch,
  // cache, or claim the production reader's scope. No SW on staging also
  // means it always loads fresh.
  if ("serviceWorker" in navigator && import.meta.env.BASE_URL === "/pagevoice/") {
    // Defer past first paint so it doesn't compete with the initial render.
    //
    // 2026-09-27: this used to be a bare window.addEventListener("load", ...).
    // init() is called from a React effect in App.tsx, and on any warm load
    // (bundle served from the HTTP cache) the `load` event has ALREADY fired
    // by the time that effect runs — measured loadEventEnd 78-171 ms with the
    // effect landing after it. A listener added post-`load` never fires, so
    // register() was never called and the app shipped with NO service worker
    // at all: no precache, no offline shell, and a home-screen launch with a
    // cold or flaky network had nothing to fall back on. Verified against the
    // live deploy — getRegistrations() returned [] and caches.keys() returned
    // [] after three consecutive full loads.
    //
    // Run now if the document is already loaded; otherwise wait for `load`.
    // ── CAPTURE THE INSTALL PROMPT (2026-10-07) ─────────────────────
    // installPromptEvent was declared, read by canPrompt(), cleared in two
    // places — and NEVER ASSIGNED. No beforeinstallprompt listener existed
    // anywhere, so canPrompt() could only ever return false and the install
    // route was dead regardless of the banner flags.
    //
    // Found by grepping the BUILT bundle for the event name and getting
    // zero hits, after I had already reported the prompt as "re-enabled".
    // Flipping a flag that gates markup which renders `null`, over state
    // nothing ever sets, is three layers of doing nothing.
    window.addEventListener("beforeinstallprompt", (e: Event) => {
      e.preventDefault();
      state.installPromptEvent = e as BeforeInstallPromptEvent;
      notify();
    });
    window.addEventListener("appinstalled", () => {
      state.installPromptEvent = null;
      notify();
    });

    const registerSW = () => {
      navigator.serviceWorker
        .register(SW_PATH, { scope: "/pagevoice/" })
        .then((reg) => {
          armAutoReload(); // reload on next foreground once an update lands
          // ── UPDATE WATCHDOG (2026-10-07) ─────────────────────────────
          // Thomas's BOOX sat on build 261006-2315 while five later builds
          // were live. The server was innocent: nginx serves /pagevoice/
          // and sw.js with Cache-Control: no-cache and correct ETags, the
          // deployed sw.js VERSION matched the deployed index.html, and
          // PRECACHE_ASSETS listed the current bundle. The installed worker
          // simply never picked the new one up.
          //
          // register() alone is not enough: it compares sw.js byte-for-byte
          // but some Android WebViews will not re-check the script for a
          // long time, and nothing here ever called update() or told a
          // waiting worker to take over. So:
          //   1. ask explicitly, on boot and whenever the app is brought
          //      back to the foreground
          //   2. if a worker is already waiting, tell it to skip waiting
          //   3. reload once when control actually changes hands
          const pokeUpdate = () => {
            try {
              void reg.update();
              if (reg.waiting) {
                reg.waiting.postMessage({ type: "PV_SKIP_WAITING" });
              }
            } catch {
              /* update checks are best-effort */
            }
          };
          pokeUpdate();
          document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "visible") pokeUpdate();
          });
          // ── NO controllerchange RELOAD (2026-10-07) ─────────────────
          // This used to reload on `controllerchange`, guarded by a local
          // `let reloaded = false`. That guard is per PAGE LOAD, so it
          // could not stop a loop ACROSS loads — and the staleness check
          // below was unregistering the worker, which meant every load
          // registered a fresh worker, whose install calls skipWaiting()
          // and whose activate calls clients.claim(), which fires
          // controllerchange, which reloaded, which registered again.
          //
          // MEASURED on Thomas's BOOX: `GET /pagevoice/ 304` repeating
          // every 2-3 seconds with `/lib/manifest` three times per cycle.
          // The device was reloading continuously, which is why covers
          // never finished loading and taps went nowhere — not a layout
          // bug at all. Reverting the library did not help because the
          // library was never the cause.
          //
          // The staleness check below compares actual bundle filenames and
          // reloads at most once per new build, which is the only reload
          // this app needs.
          // Track update availability.
          if (reg.waiting) {
            reg.waiting.postMessage({ type: "PV_SKIP_WAITING" });
            state.updateReady = true;
            notify();
          }
          reg.addEventListener("updatefound", () => {
            const installing = reg.installing;
            if (!installing) return;
            installing.addEventListener("statechange", () => {
              if (
                installing.state === "installed" &&
                navigator.serviceWorker.controller
              ) {
                state.updateReady = true;
                notify();
              }
            });
          });
        })
        .catch((err) => {
          // SW registration failure shouldn't break the app.
          console.warn("[pagevoice] SW registration failed", err);
        });

      // ── INDEPENDENT STALENESS CHECK ─────────────────────────────────
      // Belt and braces, deliberately NOT routed through the service
      // worker: if the SW machinery is the thing that is wedged, asking it
      // nicely cannot help. This compares the bundle filename the server is
      // serving right now against the one this document actually loaded. A
      // mismatch means we are running an old build, whatever the SW thinks.
      //
      // cache:"no-store" keeps it off both the HTTP cache and the SW cache.
      // On a mismatch: drop every pv- cache and
      // reload once. The sessionStorage guard means a device that somehow
      // still comes back stale reloads exactly once per launch rather than
      // spinning.
      const checkForStaleBuild = async () => {
        try {
          const loaded = Array.from(document.querySelectorAll("script[src]"))
            .map((s) => (s as HTMLScriptElement).src)
            .find((src) => /\/assets\/index-[A-Za-z0-9_-]+\.js$/.test(src));
          if (!loaded) return;
          const res = await fetch(
            `${import.meta.env.BASE_URL}index.html?cb=${Date.now()}`,
            { cache: "no-store" },
          );
          if (!res.ok) return;
          const html = await res.text();
          const m = html.match(/assets\/index-[A-Za-z0-9_-]+\.js/);
          if (!m) return;
          if (loaded.endsWith(m[0])) return; // current — nothing to do
          // Guard on the SERVER's bundle name, in localStorage so it
          // survives reloads and relaunches. One reload per new build,
          // ever — if we come back still stale, we log and stop.
          const guardKey = "pv.staleReloadFor";
          if (localStorage.getItem(guardKey) === m[0]) {
            console.warn(
              "[pagevoice] still stale after a forced reload — not looping.",
              { loaded, server: m[0] },
            );
            return;
          }
          localStorage.setItem(guardKey, m[0]);
          console.warn("[pagevoice] stale build detected, refreshing", {
            loaded,
            server: m[0],
          });
          try {
            const keys = await caches.keys();
            await Promise.all(
              keys.filter((k) => k.startsWith("pv-")).map((k) => caches.delete(k)),
            );
          } catch {
            /* best effort */
          }
          // DELIBERATELY NOT unregistering the service worker. Doing so
          // made every subsequent load register a brand-new worker, whose
          // activate() claims the page and fires controllerchange — the
          // other half of the reload loop. Dropping the pv- caches is
          // enough: the next load fetches the new shell over the network
          // (the navigation handler is network-first).
          window.location.reload();
        } catch {
          /* offline, or the check failed — never break the app for this */
        }
      };
      void checkForStaleBuild();
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") void checkForStaleBuild();
      });

      // Listen for messages from the SW (new HTML available, etc).
      navigator.serviceWorker.addEventListener("message", (ev) => {
        if (ev.data?.type === "pv-html-updated") {
          state.updateReady = true;
          notify();
        }
      });
    };

    if (document.readyState === "complete") {
      // `load` already fired — register on the next macrotask so we still
      // stay out of the way of the current render, but never miss the boat.
      setTimeout(registerSW, 0);
    } else {
      window.addEventListener("load", registerSW, { once: true });
    }
  }
}

/** Skip the SW waiting state and reload — used by the "Update" banner. */
export async function applyUpdateAndReload(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    window.location.reload();
    return;
  }
  const reg = await navigator.serviceWorker.getRegistration();
  if (reg?.waiting) {
    reg.waiting.postMessage({ type: "PV_SKIP_WAITING" });
  }
  // Give the SW a moment to take control, then reload.
  setTimeout(() => window.location.reload(), 300);
}
